import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { bulletinBoardService, BulletinBoardEntry } from '../services/bulletin-board.service'
import { MessageSquare } from 'lucide-react'
import { useLocale } from '../hooks/use-locale'
import { pagePanel, pagePanelHeader } from './layout/page-styles'

interface BulletinBoardDisplayProps {
  companyId: string
}

export function BulletinBoardDisplay({ companyId }: BulletinBoardDisplayProps) {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const [entries, setEntries] = useState<BulletinBoardEntry[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true)
      const data = await bulletinBoardService.getAllByCompany(companyId)
      setEntries(data)
    } catch (error) {
      console.error('Failed to load bulletin board entries:', error)
    } finally {
      setLoading(false)
    }
  }, [companyId])

  useEffect(() => {
    loadEntries()
  }, [loadEntries])

  useEffect(() => {
    if (entries.length <= 1) return
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % entries.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [entries.length])

  if (loading || entries.length === 0) return null

  const currentEntry = entries[currentIndex]

  return (
    <div className={pagePanel}>
      <div className={`${pagePanelHeader} flex items-center gap-2`}>
        <MessageSquare className="w-4 h-4 text-primary-600" />
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          {t('bulletin.displayLabel')}
        </span>
      </div>
      <div className="px-5 py-4">
        <p className="text-base text-neutral-800 leading-relaxed">{currentEntry.content}</p>
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-neutral-500">
            {currentEntry.createdByName} &middot;{' '}
            {formatDate(currentEntry.createdAt)}
          </span>
          {entries.length > 1 && (
            <div className="flex gap-1.5">
              {entries.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`Entry ${index + 1}`}
                  className={`rounded-full transition-all ${
                    index === currentIndex
                      ? 'w-5 h-2 bg-primary-500'
                      : 'w-2 h-2 bg-primary-100 hover:bg-primary-200'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
